import PushSheet from '@/components/PushSheet';
import { useReservationList } from '@/features/reservations/hooks/useReservations';
import { useT } from '@/i18n';
import type { TableSession } from '@restaurant/api-client';
import type { Reservation } from '@restaurant/api-client';
import {
  Block,
  Link as F7Link,
  List,
  ListItem,
  PageContent,
  Preloader,
  Toolbar,
  f7,
} from 'framework7-react';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { useAttachReservation } from '../hooks/useTableOrdering';

/**
 * Attach an arrived reservation to this tab - backfills the guest's name/
 * phone and marks the reservation arrived (reuses the reservation arrival
 * use-case server-side). Picking a row is the action, so there's no Save in
 * the toolbar; the backend is the authority on what's attachable.
 *
 * Structured per FRONTEND_PRACTICES Rule 10: the stateless wrapper renders
 * `<PushSheet>` + the `<Toolbar>` as a direct child; the query/list live in a
 * `*Content` child so the wrapper never re-renders mid-animation.
 */
export default function AttachReservationSheet({
  session,
  open,
  onClose,
}: {
  session: TableSession;
  open: boolean;
  onClose: () => void;
}) {
  const tAll = useT();
  const t = tAll.tableOrdering.attachReservation;
  return (
    <PushSheet open={open} onClose={onClose} style={{ height: '76vh' }}>
      <Toolbar>
        <div className="left">
          <F7Link sheetClose>{tAll.cancel}</F7Link>
        </div>
        <div className="title">{t.title}</div>
        <div className="right" />
      </Toolbar>
      <AttachReservationContent session={session} />
    </PushSheet>
  );
}

function AttachReservationContent({ session }: { session: TableSession }) {
  const tAll = useT();
  const t = tAll.tableOrdering.attachReservation;
  const today = DateTime.now().toFormat('yyyy-MM-dd');
  const { data, isLoading } = useReservationList(today);
  const attach = useAttachReservation();

  const candidates = useMemo(() => {
    const list: Reservation[] = data?.data ?? [];
    return list
      .filter(
        (r) =>
          (r.status === 'CONFIRMED' || r.status === 'PENDING') && !r.completedAt && !r.cancelledAt,
      )
      .sort((a, b) => {
        const at = a.tableIds.includes(session.tableId) ? 0 : 1;
        const bt = b.tableIds.includes(session.tableId) ? 0 : 1;
        return at - bt || a.time.localeCompare(b.time);
      });
  }, [data, session.tableId]);

  function pick(r: Reservation) {
    f7.dialog.confirm(t.confirmAttach(r.guest.name), t.title, () => {
      attach.mutate(
        { sessionId: session.id, body: { reservationId: r.id } },
        {
          onSuccess: () => {
            f7.toast.show({
              text: tAll.tableOrdering.timeline.reservationAttached,
              closeTimeout: 2000,
            });
            f7.sheet.close();
          },
        },
      );
    });
  }

  return (
    <PageContent>
      {isLoading ? (
        <Block className="text-align-center" style={{ paddingTop: 40 }}>
          <Preloader />
        </Block>
      ) : candidates.length === 0 ? (
        <Block className="text-align-center text-color-gray" style={{ paddingTop: 40 }}>
          {t.noAttachable}
        </Block>
      ) : (
        <List dividers strongIos insetIos>
          {candidates.map((r) => {
            const onThisTable = r.tableIds.includes(session.tableId);
            return (
              <ListItem
                key={r.id}
                link="#"
                noChevron
                title={r.guest.name}
                footer={`${t.partyOf(r.partySize)}${onThisTable ? t.onThisTable : ''}`}
                after={DateTime.fromISO(r.time).toFormat('HH:mm')}
                onClick={() => pick(r)}
              />
            );
          })}
        </List>
      )}
    </PageContent>
  );
}
